// "Uninstall app" from the account menu in the Wix dashboard (Robert, 28.10.2026). The owner says why first – that
// is the point of it: knowing why customers leave. An answer that is no reason at all ("a", "asdf") is turned away
// once by the backend (src/controller/wix_uninstall.py); the next answer always goes through. Then the owner
// deletes the app on the site's Manage Apps page in Wix: Wix lets only the owner remove an app, not the app itself.
// Never in the way: if the request fails, the owner still gets to Manage Apps. The last step shows where to click
// there (WixUninstallGuide), since Wix opens in a new tab.
import { useState } from 'react';
import { Modal, Box, Button } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { authFetch } from '../utils/api';
import WixUninstallGuide from './WixUninstallGuide';

const API = process.env.REACT_APP_API_BASE_URL;
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ALL_SITES = 'https://manage.wix.com/account/sites';

const manageAppsUrl = (siteId) =>
  GUID.test(siteId || '') ? `https://manage.wix.com/dashboard/${siteId}/manage-apps` : ALL_SITES;

const outlined = {
  color: '#5616ea',
  borderColor: '#5616ea',
  '&:hover': { borderColor: '#5e0fb3', backgroundColor: '#f3eaff' },
};
const danger = {
  backgroundColor: '#dc2626',
  '&:hover': { backgroundColor: '#b91c1c' },
};

const WixUninstallModal = ({ open, onClose, wixSite }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [step, setStep] = useState('reason'); // reason | done
  const [reason, setReason] = useState('');
  const [rejected, setRejected] = useState(false);
  const [sending, setSending] = useState(false);
  const [manageUrl, setManageUrl] = useState('');
  const [opened, setOpened] = useState(false);

  const close = () => {
    onClose();
    setStep('reason');
    setReason('');
    setRejected(false);
    setSending(false);
    setOpened(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim() || sending) return;
    setSending(true);
    let url = manageAppsUrl(wixSite?.siteId);
    try {
      const r = await authFetch(`${API}wix/uninstall/reason`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reason: reason.trim(),
          wix_instance_id: wixSite?.instanceId || null,
          site_id: wixSite?.siteId || null,
        }),
      });
      if (r && r.ok) {
        const j = await r.json();
        if (j.accepted === false) {
          setRejected(true);
          setSending(false);
          return;
        }
        if (j.manage_url) url = j.manage_url;
      }
    } catch (err) {
      // the owner goes on to Manage Apps anyway
    }
    setManageUrl(url);
    setSending(false);
    setStep('done');
  };

  return (
    <Modal open={open} onClose={close}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'white',
          borderRadius: '10px',
          boxShadow: 24,
          padding: isMobile ? '20px' : '28px',
          width: isMobile ? '80%' : '90%',
          maxWidth: 440,
          maxHeight: 'calc(100vh - 88px)',
          overflowY: 'auto',
          outline: 'none',
        }}
      >
        {step === 'reason' ? (
          <form onSubmit={submit}>
            <h2 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600 }}>
              {t('wix_uninstall_title')}
            </h2>
            <p style={{ margin: '0 0 14px 0', fontSize: '14px', color: '#6b7280', lineHeight: 1.5 }}>
              {t('wix_uninstall_desc')}
            </p>
            <textarea
              autoFocus
              rows={4}
              maxLength={2000}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (rejected) setRejected(false);
              }}
              placeholder={t('wix_uninstall_placeholder')}
              aria-invalid={rejected}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                resize: 'vertical',
                padding: '10px 12px',
                fontSize: '14px',
                fontFamily: 'inherit',
                lineHeight: 1.5,
                border: `1px solid ${rejected ? '#dc2626' : '#d1d5db'}`,
                borderRadius: '8px',
                outline: 'none',
              }}
            />
            {/* Reserved line, so the error never moves the buttons. */}
            <p role="alert" style={{ minHeight: '20px', margin: '6px 0 14px 0', fontSize: '13px', color: '#dc2626' }}>
              {rejected ? t('wix_uninstall_error') : ''}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <Button onClick={close} variant="outlined" sx={outlined}>
                {t('cancel')}
              </Button>
              <Button type="submit" variant="contained" disabled={!reason.trim() || sending} sx={danger}>
                {t('wix_uninstall_continue')}
              </Button>
            </div>
          </form>
        ) : (
          <>
            <h2 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600 }}>
              {t('wix_uninstall_done_title')}
            </h2>
            <p style={{ margin: '0 0 14px 0', fontSize: '14px', color: '#6b7280', lineHeight: 1.5 }}>
              {t('wix_uninstall_done_desc')}
            </p>
            <WixUninstallGuide opened={opened} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <Button onClick={close} variant="outlined" sx={outlined}>
                {t('close')}
              </Button>
              <Button
                component="a"
                href={manageUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpened(true)}
                variant="contained"
                sx={danger}
              >
                {t('wix_uninstall_open_manage')}
              </Button>
            </div>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default WixUninstallModal;
